import {
  Box,
  CardContent,
  Slider,
  Typography,
  CircularProgress,
  Button,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import CytoscapeComponent from 'react-cytoscapejs';
import { useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { updateTransaction } from '../../redux/features/filter/transactionSlice';
import { clearSelectedTransaction, updateSelectedTransaction } from '../../redux/features/selectedTransaction/selectedTransactionSlice';
import {
  buildGraphCacheKey,
  clearGraphDataCache,
  fetchGraphDataWithCache,
  getGraphCacheMetrics,
  resetGraphCacheMetrics,
} from '../../helpers/graphDataCache';
import { fetchProcessedGraphResult } from '../../helpers/graphRequest';
import { cloneCytoscapeValue, toMutableCytoscapeElements } from '../../helpers/cytoscapeElements';
import {
  MAX_NODES,
  GRAPH_CACHE_TTL_MS,
  GRAPH_CACHE_MAX_ENTRIES,
  IS_DEVELOPMENT,
  EXPANSION_HOPS_LIMIT,
  GRAPH_VIEWPORT_HEIGHT,
  GRAPH_ZOOM_STEP,
  GRAPH_PAN_STEP,
  GRAPH_RING_BASE_RADIUS,
  GRAPH_RING_RADIUS_STEP,
  EXPANSION_NODE_SCALE_FACTOR,
  EXPANSION_NODE_MAX_DISTANCE,
  EXPANSION_RING_BUFFER,
  EXPANSION_RING_MIN_SPACING,
  GRAPH_FIT_PADDING,
  GRAPH_EXPANSION_FIT_PADDING,
} from '../../helpers/graphConstants';
import { activeStylesheet } from './graphStylesheet';
import { getElementKey, getNeighborPositionForNode, buildRingLayoutPositions } from '../../helpers/graphLayoutUtils';
import { joinClassNames, normalizeGraphPayload, normalizeGraphSourcesForMerge, mergeNormalizedGraphParts } from '../../helpers/graphDataTransform';
import { downloadGraphAsExcel } from '../../helpers/graphExcelExport';
import { buildExpansionAppendMutation, calculateExpandedNodePosition } from '../../helpers/graphExpansionUtils';
import { centerVisibleGraph, fitVisibleGraph, panGraph, runGraphKeyboardAction, zoomGraph } from '../../helpers/graphNavigation';
import { applyGraphSearchFilter, resetGraphSearchView } from '../../helpers/graphSearch';
import {
  createApiGraphPerfMetrics,
  createErrorGraphPerfMetrics,
  createGraphPerfMetrics,
  createRenderGraphPerfMetrics,
} from '../../helpers/graphPerformance';

const GraphComponent = ({ transactionData, onGraphNodesChange, onGraphEdgesChange, onCurrentCenterNodeChange }) => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const selectedTransactionId = useSelector((state) => state.selectedTransaction.transactionId);
  const selectedTransactionData = useSelector((state) => state.selectedTransaction.transactionData);
  const [levelFilter, setLevelFilter] = useState(1);
  const [isLayoutRunning, setIsLayoutRunning] = useState(false);
  const [graphData, setGraphData] = useState(null);
  const [loadingGraph, setLoadingGraph] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [nodeCount, setNodeCount] = useState(0);
  const [edgeCount, setEdgeCount] = useState(0);
  const [visibleNodeCount, setVisibleNodeCount] = useState(0);
  const [isGraphReady, setIsGraphReady] = useState(false);
  const [currentCenterNodeId, setCurrentCenterNodeId] = useState(transactionData?.personDocument || id);
  const [isDrilldownMode, setIsDrilldownMode] = useState(false);
  const [expandedGraphData, setExpandedGraphData] = useState([]);
  const [expandedNodeIds, setExpandedNodeIds] = useState([]);
  const [pendingNodeNavigation, setPendingNodeNavigation] = useState(null);
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [previewGraphData, setPreviewGraphData] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [cacheMetrics, setCacheMetrics] = useState(() => getGraphCacheMetrics());
  const [graphPerfMetrics, setGraphPerfMetrics] = useState(() => createGraphPerfMetrics());
  const cyRef = useRef(null);
  const previousElementsRef = useRef([]);
  const graphMutationRef = useRef({ type: 'reset' });
  const appendedNodePositionsRef = useRef(new Map());
  const graphPerfRef = useRef({
    activeRequestId: null,
    requestCounter: 0,
    requestStartedAt: null,
    renderStartedAt: null,
  });

  const [debouncedLevelFilter, setDebouncedLevelFilter] = useState(1);

  const collectOperationIdFromValue = useCallback((value, depth = 0) => {
    if (depth > 4 || value === null || value === undefined) {
      return '';
    }

    if (Array.isArray(value)) {
      for (let i = 0; i < value.length; i += 1) {
        const found = collectOperationIdFromValue(value[i], depth + 1);
        if (found) {
          return found;
        }
      }
      return '';
    }

    if (typeof value === 'object') {
      const directCandidate = value.operation_id
        || value.operationId
        || value.operation
        || value.operationID
        || value.edge_operation_id
        || value.edgeOperationId
        || value.transaction_id
        || value.transactionId;

      if (directCandidate !== null && directCandidate !== undefined && String(directCandidate).trim()) {
        return String(directCandidate).trim();
      }

      const entries = Object.entries(value);
      for (let i = 0; i < entries.length; i += 1) {
        const nestedValue = entries[i][1];
        if (!nestedValue || typeof nestedValue !== 'object') {
          continue;
        }

        const found = collectOperationIdFromValue(nestedValue, depth + 1);
        if (found) {
          return found;
        }
      }
    }

    return '';
  }, []);

  const isSyntheticEdgeId = useCallback((edgeId, edgeData) => {
    if (!edgeId) {
      return true;
    }

    const sourceId = edgeData?.source ? String(edgeData.source).trim() : '';
    const targetId = edgeData?.target ? String(edgeData.target).trim() : '';
    const normalizedId = String(edgeId).trim();

    if (!sourceId || !targetId) {
      return false;
    }

    return normalizedId === `${sourceId}-${targetId}` || normalizedId === `${targetId}-${sourceId}`;
  }, []);

  const resolveEdgeOperationId = useCallback((edgeData) => {
    const candidate = collectOperationIdFromValue(edgeData);

    if (candidate === null || candidate === undefined) {
      return '';
    }

    const normalizedCandidate = String(candidate).trim();

    if (normalizedCandidate) {
      return normalizedCandidate;
    }

    const fallbackId = edgeData?.id;
    if (!fallbackId || isSyntheticEdgeId(fallbackId, edgeData)) {
      return '';
    }

    return String(fallbackId).trim();
  }, [collectOperationIdFromValue, isSyntheticEdgeId]);

  const clearPreviewState = useCallback(() => {
    setPreviewGraphData(null);
    setIsPreviewLoading(false);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedLevelFilter(levelFilter), 400);
    return () => clearTimeout(t);
  }, [levelFilter]);

  const effectiveHops = isDrilldownMode ? Math.min(debouncedLevelFilter, 2) : debouncedLevelFilter;

  useEffect(() => {
    const initialNodeId = transactionData?.personDocument || id;
    setCurrentCenterNodeId(initialNodeId);
    setIsDrilldownMode(false);
    setExpandedGraphData([]);
    setExpandedNodeIds([]);
    appendedNodePositionsRef.current = new Map();
    setIsConfirmDialogOpen(false);
    setPendingNodeNavigation(null);
    clearPreviewState();
    setIsGraphReady(false);
    graphMutationRef.current = { type: 'reset' };
  }, [clearPreviewState, id, transactionData?.personDocument]);

  useEffect(() => {
    return () => {
      cyRef.current = null;
      setIsGraphReady(false);
    };
  }, []);

  useEffect(() => {
    if (onCurrentCenterNodeChange) {
      onCurrentCenterNodeChange(currentCenterNodeId);
    }
  }, [currentCenterNodeId, onCurrentCenterNodeChange]);

  const fetchGraphPayload = useCallback(async (nodeId, hops) => {
    const cacheKey = buildGraphCacheKey({
      scope: 'details-graph',
      nodeId,
      hops,
    });

    const result = await fetchGraphDataWithCache({
      key: cacheKey,
      ttlMs: GRAPH_CACHE_TTL_MS,
      maxEntries: GRAPH_CACHE_MAX_ENTRIES,
      fetcher: async () => {
        const graphData = await fetchProcessedGraphResult(
          nodeId,
          hops,
          500,
          {
            apiUrl: process.env.REACT_APP_API_GRAPH_URL,
            apiKey: process.env.REACT_APP_API_GRAPH_KEY,
          }
        );

        return graphData;
      },
    });

    const metrics = getGraphCacheMetrics();
    setCacheMetrics(metrics);

    if (IS_DEVELOPMENT) {
      console.info('[graph-cache]', {
        source: result.source,
        durationMs: result.durationMs,
        nodeId,
        hops,
        hitRate: metrics.hitRate,
        networkRequests: metrics.networkRequests,
        cacheHits: metrics.cacheHits,
      });
    }

    return result;
  }, []);

  useEffect(() => {
    const fetchGraphData = async () => {
      if (!currentCenterNodeId) return;

      try {
        setIsGraphReady(false);
        setLoadingGraph(true);
        graphMutationRef.current = { type: 'reset' };
        setExpandedGraphData([]);
        setExpandedNodeIds([]);
        appendedNodePositionsRef.current = new Map();
        setIsConfirmDialogOpen(false);
        setPendingNodeNavigation(null);
        clearPreviewState();

        graphPerfRef.current.requestCounter += 1;
        const requestId = graphPerfRef.current.requestCounter;
        const requestStartedAt = performance.now();

        graphPerfRef.current.activeRequestId = requestId;
        graphPerfRef.current.requestStartedAt = requestStartedAt;
        graphPerfRef.current.renderStartedAt = null;

        setGraphPerfMetrics(createGraphPerfMetrics());

        const graphResult = await fetchGraphPayload(currentCenterNodeId, effectiveHops);
        const responseArrivedAt = performance.now();

        if (graphPerfRef.current.activeRequestId === requestId) {
          graphPerfRef.current.renderStartedAt = responseArrivedAt;

          setGraphPerfMetrics(createApiGraphPerfMetrics({
            source: graphResult.source,
            requestStartedAt,
            responseArrivedAt,
          }));
        }

        setGraphData(graphResult.data);
      } catch (error) {
        setCacheMetrics(getGraphCacheMetrics());
        setGraphPerfMetrics(createErrorGraphPerfMetrics());
        setGraphData(null);
      } finally {
        setLoadingGraph(false);
      }
    };

    fetchGraphData();
  }, [clearPreviewState, currentCenterNodeId, effectiveHops, fetchGraphPayload]);

  const allElements = useMemo(() => {
    const destinyNodeId = transactionData?.destinyDocument;
    const sources = normalizeGraphSourcesForMerge({
      graphData,
      transactionGraphData: transactionData?.graphData,
      expandedGraphData,
      previewGraphData,
      currentCenterNodeId,
      destinyNodeId,
    });

    return mergeNormalizedGraphParts(sources, currentCenterNodeId);
  }, [graphData, transactionData, currentCenterNodeId, expandedGraphData, previewGraphData]);

  const filteredElements = useMemo(() => {
    if (!allElements || !Array.isArray(allElements) || allElements.length === 0) {
      return { elements: [], shouldShowWarning: false, totalNodes: 0 };
    }

    const nodes = allElements.filter(el => el.data && !el.data.source && !el.data.target);
    const edges = allElements.filter(el => el.data && (el.data.source || el.data.target));

    if (nodes.length > MAX_NODES) {
      return { elements: [], shouldShowWarning: true, totalNodes: nodes.length };
    }

    const edgeMap = new Map();
    edges.forEach(edge => {
      const sourceId = edge.data.source;
      const targetId = edge.data.target;

      if (!edgeMap.has(sourceId)) edgeMap.set(sourceId, 0);
      if (!edgeMap.has(targetId)) edgeMap.set(targetId, 0);

      edgeMap.set(sourceId, edgeMap.get(sourceId) + 1);
      edgeMap.set(targetId, edgeMap.get(targetId) + 1);
    });

    const nodesWithNeighbors = nodes.filter(node => {
      const neighborCount = edgeMap.get(node.data.id) || 0;
      return neighborCount > 0;
    });

    return {
      elements: [...nodesWithNeighbors, ...edges],
      shouldShowWarning: false,
      totalNodes: nodes.length
    };
  }, [allElements]);

  const rawElements = filteredElements.elements;
  const exceedsLimit = filteredElements.shouldShowWarning;
  const totalNodeCount = filteredElements.totalNodes;
  const hasExpandedBranches = expandedGraphData.length > 0;

  const previewFocusSets = useMemo(() => {
    if (!previewGraphData || !pendingNodeNavigation?.nodeId) {
      return {
        enabled: false,
        nodeIds: new Set(),
        edgeIds: new Set(),
      };
    }

    const previewElements = normalizeGraphPayload({
      dataSource: previewGraphData,
      currentCenterNodeId,
      destinyNodeId: transactionData?.destinyDocument,
    });

    const nodeIds = new Set([pendingNodeNavigation.nodeId]);
    const edgeIds = new Set();

    const normalizedPreviewElements = [
      ...(previewElements?.nodes || []),
      ...(previewElements?.edges || []),
    ];

    normalizedPreviewElements.forEach((element) => {
      const elementId = element?.data?.id;
      if (!elementId) return;

      if (element?.data?.source || element?.data?.target) {
        edgeIds.add(elementId);
        return;
      }

      nodeIds.add(elementId);
    });

    return {
      enabled: true,
      nodeIds,
      edgeIds,
    };
  }, [currentCenterNodeId, pendingNodeNavigation?.nodeId, previewGraphData, transactionData?.destinyDocument]);

  const elements = useMemo(() => {
    if (!rawElements || rawElements.length === 0) {
      return [];
    }

    const nodeElements = rawElements.filter((element) => element?.data && !element.data.source && !element.data.target);
    const edgeElements = rawElements.filter((element) => element?.data && (element.data.source || element.data.target));

    const applyPreviewClass = (element, elementType) => {
      if (!previewFocusSets.enabled) {
        return element;
      }

      const elementId = element?.data?.id;
      if (!elementId) {
        return element;
      }

      const isFocused = elementType === 'node'
        ? previewFocusSets.nodeIds.has(elementId)
        : previewFocusSets.edgeIds.has(elementId);

      return {
        ...element,
        classes: joinClassNames(element.classes, isFocused ? 'preview-focus' : 'preview-muted'),
      };
    };

    const centerNode = nodeElements.find((element) => element?.data?.id === currentCenterNodeId);
    const positionedNodes = [];

    if (centerNode) {
      positionedNodes.push(applyPreviewClass({ ...centerNode, position: { x: 0, y: 0 } }, 'node'));
    }

    nodeElements.forEach((element) => {
      const nodeId = element?.data?.id;
      if (!nodeId || nodeId === currentCenterNodeId) {
        return;
      }

      const persistedPosition = appendedNodePositionsRef.current.get(nodeId);
      if (!persistedPosition) {
        return;
      }

      positionedNodes.push(applyPreviewClass({
        ...element,
        position: persistedPosition,
      }, 'node'));
    });

    const ringNodes = nodeElements.filter((element) => {
      const nodeId = element?.data?.id;
      return nodeId && nodeId !== currentCenterNodeId && !appendedNodePositionsRef.current.has(nodeId);
    });
    const ringPositions = buildRingLayoutPositions(ringNodes);

    ringNodes.forEach((element) => {
      const nodeId = element?.data?.id;
      const position = nodeId ? ringPositions.get(nodeId) : null;
      if (!position) {
        return;
      }

      positionedNodes.push({
        ...applyPreviewClass(element, 'node'),
        position,
      });
    });

    const styledEdges = edgeElements.map((element) => applyPreviewClass(element, 'edge'));

    return toMutableCytoscapeElements([...positionedNodes, ...styledEdges]);
  }, [rawElements, currentCenterNodeId, previewFocusSets]);

  const graphNodes = useMemo(() => {
    if (!elements || elements.length === 0) {
      return [];
    }

    return elements
      .filter(el => el.data && !el.data.source && !el.data.target)
      .map(node => ({
        id: node.data.id,
        label: node.data.label || node.data.id
      }));
  }, [elements]);

  const graphEdges = useMemo(() => {
    if (!allElements || !Array.isArray(allElements) || allElements.length === 0) {
      return [];
    }

    return allElements
      .filter(el => el.data && (el.data.source || el.data.target))
      .map(edge => ({
        ...edge.data
      }));
  }, [allElements]);

  useEffect(() => {
    if (onGraphNodesChange && graphNodes) {
      onGraphNodesChange(graphNodes);
    }
  }, [graphNodes, onGraphNodesChange]);

  useEffect(() => {
    if (onGraphEdgesChange && graphEdges) {
      onGraphEdgesChange(graphEdges);
    }
  }, [graphEdges, onGraphEdgesChange]);

  const handleExportToExcel = useCallback(async () => {
    if (!allElements || allElements.length === 0) return;
    await downloadGraphAsExcel(allElements, id);
  }, [allElements, id]);

  const handleCyInit = useCallback((cy) => {
    if (!cy) return;
    cyRef.current = cy;
    setIsGraphReady(true);

    cy.on('layoutstart', () => setIsLayoutRunning(true));
    cy.on('layoutstop', () => {
      setIsLayoutRunning(false);

      const renderMetrics = createRenderGraphPerfMetrics({
        requestStartedAt: graphPerfRef.current.requestStartedAt,
        renderStartedAt: graphPerfRef.current.renderStartedAt,
        renderFinishedAt: performance.now(),
      });

      if (renderMetrics) {
        setGraphPerfMetrics((previous) => ({
          ...previous,
          ...renderMetrics,
        }));
        graphPerfRef.current.renderStartedAt = null;
      }
    });

    cy.minZoom(0.3);
    cy.maxZoom(4);

    let lastAppliedScale = null;
    const applyZoomTypography = () => {
      if (!cy || cy.destroyed()) return;

      const zoom = cy.zoom();
      const clampedZoom = Math.max(0.3, Math.min(1, zoom));
      const scale = 0.82 + (((clampedZoom - 0.3) / 0.7) * 0.18);

      if (lastAppliedScale !== null && Math.abs(scale - lastAppliedScale) < 0.03) {
        return;
      }

      lastAppliedScale = scale;

      const nodeBaseFontSize = `${Math.max(12, Math.round(16 * scale))}px`;
      const nodeHop0FontSize = `${Math.max(14, Math.round(19 * scale))}px`;
      const nodeHop1FontSize = `${Math.max(12, Math.round(16 * scale))}px`;
      const nodeHop2FontSize = `${Math.max(11, Math.round(14 * scale))}px`;
      const nodeHop3FontSize = `${Math.max(10, Math.round(12 * scale))}px`;
      const nodeHop3SelectedFontSize = `${Math.max(11, Math.round(13 * scale))}px`;
      const edgeBaseFontSize = `${Math.max(8, Math.round(10 * scale))}px`;
      const edgeHop3FontSize = `${Math.max(7, Math.round(8 * scale))}px`;

      try {
        cy.style()
          .selector('node').style('font-size', nodeBaseFontSize)
          .selector("node[hop='0']").style('font-size', nodeHop0FontSize)
          .selector("node[hop='1']").style('font-size', nodeHop1FontSize)
          .selector("node[hop='2']").style('font-size', nodeHop2FontSize)
          .selector('node[hop >= 3]').style('font-size', nodeHop3FontSize)
          .selector('node[hop >= 3]:selected').style('font-size', nodeHop3SelectedFontSize)
          .selector('edge').style('font-size', edgeBaseFontSize)
          .selector('edge[source][hop >= 3], edge[target][hop >= 3]').style('font-size', edgeHop3FontSize)
          .selector('edge.edge-out').style('line-color', '#E53935').style('target-arrow-color', '#E53935')
          .selector('edge.edge-in').style('line-color', '#43A047').style('target-arrow-color', '#43A047')
          .selector('edge.transaction-selected').style('line-color', '#FDD835').style('target-arrow-color', '#FDD835')
          .selector('edge.edge-out.transaction-selected, edge.edge-in.transaction-selected').style('line-color', '#FDD835').style('target-arrow-color', '#FDD835')
          .update();
      } catch (error) {
      }
    };

    applyZoomTypography();
    cy.on('zoom', applyZoomTypography);

    cy.userPanningEnabled(true);
    cy.userZoomingEnabled(true);
    cy.boxSelectionEnabled(true);

    cy.on('tap', 'node', (event) => {
      const clickedNode = event.target;
      const clickedNodeId = clickedNode.id();

      if (!clickedNodeId || clickedNodeId === currentCenterNodeId || loadingGraph || isPreviewLoading || expandedNodeIds.includes(clickedNodeId)) {
        return;
      }

      const nodeDegree = clickedNode.connectedEdges().length;
      const isTerminalNode = nodeDegree <= 1;

      if (!isTerminalNode) {
        return;
      }

      const clickedPosition = clickedNode.position();
      const parentPosition = getNeighborPositionForNode(clickedNode);
      const movedPosition = calculateExpandedNodePosition({
        clickedPosition,
        parentPosition,
        graphRingBaseRadius: GRAPH_RING_BASE_RADIUS,
        expansionNodeScaleFactor: EXPANSION_NODE_SCALE_FACTOR,
        expansionNodeMaxDistance: EXPANSION_NODE_MAX_DISTANCE,
      });

      clickedNode.position(movedPosition);
      appendedNodePositionsRef.current.set(clickedNodeId, movedPosition);

      const expandAfterMove = async () => {
        if (expandedNodeIds.includes(clickedNodeId)) return;
        try {
          setLoadingGraph(true);
          const expandedResult = await fetchGraphPayload(
            clickedNodeId,
            Math.min(levelFilter, EXPANSION_HOPS_LIMIT),
          );
          const expandedData = expandedResult.data;
          const { mutation } = buildExpansionAppendMutation({
            expandedData,
            anchorNodeId: clickedNodeId,
            currentCenterNodeId,
            destinyNodeId: transactionData?.destinyDocument,
            existingNodeIds: cy.nodes().map((node) => node.id()),
            expansionRingBaseRadius: GRAPH_RING_BASE_RADIUS,
            expansionRingBuffer: EXPANSION_RING_BUFFER,
            expansionRingMinSpacing: EXPANSION_RING_MIN_SPACING,
          });

          graphMutationRef.current = mutation;

          setExpandedGraphData((previous) => ([
            ...previous,
            {
              nodeId: clickedNodeId,
              data: expandedData,
              hops: Math.min(levelFilter, EXPANSION_HOPS_LIMIT),
            },
          ]));
          setExpandedNodeIds((previous) => [...previous, clickedNodeId]);
          setIsDrilldownMode(false);
          dispatch(updateTransaction(''));
          dispatch(clearSelectedTransaction());
        } catch (error) {
          setCacheMetrics(getGraphCacheMetrics());
        } finally {
          clearPreviewState();
          setLoadingGraph(false);
        }
      };

      expandAfterMove();
    });

    cy.on('tap', 'edge', (event) => {
      const clickedEdge = event.target;
      const clickedEdgeData = cloneCytoscapeValue(clickedEdge.data());
      const clickedOperationId = resolveEdgeOperationId(clickedEdgeData);

      if (!clickedOperationId || loadingGraph || isPreviewLoading) {
        if (!clickedOperationId && process.env.NODE_ENV === 'development') {
          console.warn('Graph edge click without operation id', clickedEdgeData);
        }
        return;
      }

      dispatch(updateTransaction(''));
      dispatch(updateSelectedTransaction({
        transactionId: clickedOperationId,
        transactionData: clickedEdgeData,
      }));
    });
  }, [clearPreviewState, currentCenterNodeId, dispatch, expandedNodeIds, fetchGraphPayload, isPreviewLoading, levelFilter, loadingGraph, resolveEdgeOperationId, transactionData?.destinyDocument]);

  useEffect(() => {
    if (!cyRef.current) return;

    const cy = cyRef.current;
    if (cy.destroyed()) return;

    cy.batch(() => {
      cy.edges().removeClass('transaction-selected');

      const selectedEdgeId = selectedTransactionData?.id || selectedTransactionId;

      if (!selectedEdgeId) {
        return;
      }

      const selectedEdge = cy.getElementById(selectedEdgeId);
      if (selectedEdge && selectedEdge.length > 0) {
        selectedEdge.addClass('transaction-selected');
      }
    });
  }, [elements, selectedTransactionData, selectedTransactionId]);

  const handleConfirmNodeNavigation = useCallback(() => {
    const expandNode = async () => {
      if (!pendingNodeNavigation?.nodeId || expandedNodeIds.includes(pendingNodeNavigation.nodeId)) {
        return;
      }

      try {
        setLoadingGraph(true);
        const expandedResult = await fetchGraphPayload(
          pendingNodeNavigation.nodeId,
          pendingNodeNavigation.appliedHops
        );
        const expandedData = expandedResult.data;
        const { mutation } = buildExpansionAppendMutation({
          expandedData,
          anchorNodeId: pendingNodeNavigation.nodeId,
          currentCenterNodeId,
          destinyNodeId: transactionData?.destinyDocument,
          expansionRingBaseRadius: GRAPH_RING_BASE_RADIUS,
          expansionRingBuffer: EXPANSION_RING_BUFFER,
          expansionRingMinSpacing: EXPANSION_RING_MIN_SPACING,
        });

        graphMutationRef.current = mutation;

        setExpandedGraphData((previous) => ([
          ...previous,
          {
            nodeId: pendingNodeNavigation.nodeId,
            data: expandedData,
            hops: pendingNodeNavigation.appliedHops,
          },
        ]));
        setExpandedNodeIds((previous) => ([...previous, pendingNodeNavigation.nodeId]));
        setIsDrilldownMode(false);
        dispatch(updateTransaction(''));
        dispatch(clearSelectedTransaction());
      } catch (error) {
        setCacheMetrics(getGraphCacheMetrics());
      } finally {
        clearPreviewState();
        setIsConfirmDialogOpen(false);
        setPendingNodeNavigation(null);
        setLoadingGraph(false);
      }
    };

    expandNode();
  }, [clearPreviewState, currentCenterNodeId, dispatch, expandedNodeIds, fetchGraphPayload, pendingNodeNavigation, transactionData?.destinyDocument]);

  const handleCloseNodeNavigationDialog = useCallback(() => {
    clearPreviewState();
    setIsConfirmDialogOpen(false);
    setPendingNodeNavigation(null);
  }, [clearPreviewState]);

  const handleBackToInitialNode = useCallback(() => {
    const initialNodeId = transactionData?.personDocument || id;
    if (!initialNodeId) return;

    graphMutationRef.current = { type: 'reset' };
    setCurrentCenterNodeId(initialNodeId);
    setIsDrilldownMode(false);
    setExpandedGraphData([]);
    setExpandedNodeIds([]);
    appendedNodePositionsRef.current = new Map();
    clearPreviewState();
    setIsConfirmDialogOpen(false);
    setPendingNodeNavigation(null);
    dispatch(updateTransaction(''));
    dispatch(clearSelectedTransaction());

    if (cyRef.current && !cyRef.current.destroyed()) {
      const cy = cyRef.current;
      setTimeout(() => {
        if (cy && !cy.destroyed()) {
          cy.fit(cy.elements(':visible'), 80);
        }
      }, 200);
    }
  }, [clearPreviewState, dispatch, id, transactionData]);

  useEffect(() => {
    if (!isGraphReady) return;
    if (!cyRef.current) return;

    const cy = cyRef.current;
    if (cy.destroyed()) return;

    const isCyAlive = () => cyRef.current === cy && !cy.destroyed();

    const updateCounts = () => {
      if (!isCyAlive()) return;
      const nodes = cy.nodes();
      const edges = cy.edges();
      setNodeCount(nodes.length);
      setEdgeCount(edges.length);
      const visibleNodes = nodes.filter((node) => !node.hasClass('filtered'));
      setVisibleNodeCount(visibleNodes.length);
    };

    if (elements.length === 0) {
      if (!isCyAlive()) return;
      cy.elements().remove();
      previousElementsRef.current = [];
      setNodeCount(0);
      setEdgeCount(0);
      setVisibleNodeCount(0);
      graphMutationRef.current = { type: 'reset' };
      return;
    }

    const previousKeys = new Set(previousElementsRef.current.map(getElementKey).filter(Boolean));
    const nextKeys = new Set(elements.map(getElementKey).filter(Boolean));
    const addedElements = elements.filter((element) => {
      const key = getElementKey(element);
      return key && !previousKeys.has(key);
    });
    const removedElements = previousElementsRef.current.filter((element) => {
      const key = getElementKey(element);
      return key && !nextKeys.has(key);
    });

    const isAppendUpdate = graphMutationRef.current?.type === 'append' && removedElements.length === 0 && previousElementsRef.current.length > 0;

    if (isAppendUpdate) {
      if (!isCyAlive()) return;
      if (addedElements.length > 0) {
        cy.add(addedElements);
      }

      const anchorNodeId = graphMutationRef.current?.anchorNodeId;
      const anchorNode = anchorNodeId ? cy.getElementById(anchorNodeId) : null;
      const anchorPosition = anchorNode && anchorNode.length > 0 ? anchorNode.position() : { x: 0, y: 0 };

      const branchNodeIds = Array.isArray(graphMutationRef.current?.branchNodeIds)
        ? graphMutationRef.current.branchNodeIds
        : [];
      const branchNodesFromMetadata = branchNodeIds
        .map((nodeId) => cy.getElementById(nodeId))
        .filter((node) => node && node.length > 0);

      const addedNodes = addedElements
        .filter((element) => element?.data && !element.data.source && !element.data.target)
        .map((element) => cy.getElementById(element.data.id))
        .filter((node) => node && node.length > 0);

      const nodesToPosition = branchNodesFromMetadata.length > 0 ? branchNodesFromMetadata : addedNodes;

      if (nodesToPosition.length > 0) {
          const branchRelativePositions = graphMutationRef.current?.branchRelativePositions || {};

          nodesToPosition.forEach((cyNode, index) => {
            if (!cyNode || cyNode.length === 0) return;

            const relativePosition = branchRelativePositions[cyNode.id()];
            const pos = {
              x: anchorPosition.x + (relativePosition?.x || 0),
              y: anchorPosition.y + (relativePosition?.y || 0),
            };
            cyNode.position(pos);
            appendedNodePositionsRef.current.set(cyNode.id(), pos);
          });
      }

      const visibleAfterAppend = cy.elements(':visible');
      if (visibleAfterAppend.length > 0) {
        cy.fit(visibleAfterAppend, GRAPH_EXPANSION_FIT_PADDING);
        cy.center();
        cy.fit(visibleAfterAppend, GRAPH_EXPANSION_FIT_PADDING);
      }

      updateCounts();
      previousElementsRef.current = elements;
      graphMutationRef.current = { type: 'idle' };
      return;
    }

    if (!isCyAlive()) return;
    cy.elements().remove();
    cy.add(elements);

    const nodes = cy.nodes();
    const centerNode = cy.getElementById(currentCenterNodeId);
    if (centerNode && centerNode.length > 0) {
      centerNode.position({ x: 0, y: 0 });
    }

    const rings = new Map();
    nodes.forEach((node) => {
      if (node.id() === currentCenterNodeId) {
        return;
      }

      const hopValue = node.data('hop');
      const parsedHop = Number.parseInt(hopValue, 10);
      const ringLevel = Number.isFinite(parsedHop) && parsedHop > 0 ? parsedHop : 1;

      if (!rings.has(ringLevel)) {
        rings.set(ringLevel, []);
      }

      rings.get(ringLevel).push(node);
    });

    const ringLevels = Array.from(rings.keys()).sort((a, b) => a - b);
    ringLevels.forEach((ringLevel) => {
      const ringNodes = rings.get(ringLevel) || [];
      if (ringNodes.length === 0) {
        return;
      }

      const baseRadius = GRAPH_RING_BASE_RADIUS + ((ringLevel - 1) * GRAPH_RING_RADIUS_STEP);
      const minSpacing = 140;
      const neededRadius = (ringNodes.length * minSpacing) / (2 * Math.PI);
      const radius = Math.max(baseRadius, neededRadius);

      ringNodes.forEach((node, index) => {
        const angle = (-Math.PI / 2) + ((Math.PI * 2 * index) / ringNodes.length);
        node.position({
          x: Math.cos(angle) * radius,
          y: Math.sin(angle) * radius,
        });
      });
    });

    updateCounts();

    let secondFitTimeoutId;

    const timeoutId = setTimeout(() => {
      if (!isCyAlive()) return;

      try {
        cy.fit(undefined, GRAPH_FIT_PADDING);
        cy.center();

        secondFitTimeoutId = setTimeout(() => {
          if (!isCyAlive()) return;
          cy.fit(undefined, GRAPH_FIT_PADDING);

          const renderMetrics = createRenderGraphPerfMetrics({
            requestStartedAt: graphPerfRef.current.requestStartedAt,
            renderStartedAt: graphPerfRef.current.renderStartedAt,
            renderFinishedAt: performance.now(),
          });

          if (renderMetrics) {
            setGraphPerfMetrics((previous) => ({
              ...previous,
              ...renderMetrics,
            }));
            graphPerfRef.current.renderStartedAt = null;
          }
        }, 50);


      } catch (error) {
      }
    }, 150);

    return () => {
      clearTimeout(timeoutId);
      clearTimeout(secondFitTimeoutId);
      previousElementsRef.current = elements;
      if (graphMutationRef.current?.type !== 'append') {
        graphMutationRef.current = { type: 'idle' };
      }
    };
  }, [elements, debouncedLevelFilter, isGraphReady, currentCenterNodeId]);

  useEffect(() => {
    applyGraphSearchFilter({
      cy: cyRef.current,
      searchTerm,
      fitPadding: GRAPH_FIT_PADDING,
    });
  }, [searchTerm]);

  const handleSearch = useCallback((event) => {
    setSearchTerm(event.target.value);
  }, []);

  const handleResetView = useCallback(() => {
    setSearchTerm('');
    resetGraphSearchView(cyRef.current, GRAPH_FIT_PADDING);
  }, []);

  const handleResetCacheMetrics = useCallback(() => {
    resetGraphCacheMetrics();
    setCacheMetrics(getGraphCacheMetrics());
  }, []);

  const handleResetCacheDataAndMetrics = useCallback(() => {
    clearGraphDataCache();
    resetGraphCacheMetrics();
    setCacheMetrics(getGraphCacheMetrics());
  }, []);

  const handleZoomIn = useCallback(() => {
    zoomGraph(cyRef.current, GRAPH_ZOOM_STEP);
  }, []);

  const handleZoomOut = useCallback(() => {
    zoomGraph(cyRef.current, -GRAPH_ZOOM_STEP);
  }, []);

  const handleCenterGraph = useCallback(() => {
    centerVisibleGraph(cyRef.current);
  }, []);

  const handleFitGraph = useCallback(() => {
    fitVisibleGraph(cyRef.current, GRAPH_FIT_PADDING);
  }, []);

  const handlePanGraph = useCallback((direction) => {
    panGraph(cyRef.current, direction, GRAPH_PAN_STEP);
  }, []);

  const handleGraphKeyboardNavigation = useCallback((event) => {
    const didHandle = runGraphKeyboardAction({
      cy: cyRef.current,
      key: event.key,
      zoomStep: GRAPH_ZOOM_STEP,
      panStep: GRAPH_PAN_STEP,
      fitPadding: GRAPH_FIT_PADDING,
    });

    if (!didHandle) {
      return;
    }

    event.preventDefault();
  }, []);

  const cacheHitRatePercent = Number.isFinite(cacheMetrics.hitRate)
    ? (cacheMetrics.hitRate * 100).toFixed(1)
    : '0.0';
  const cacheAvgNetworkMs = Number.isFinite(cacheMetrics.avgNetworkTimeMs)
    ? cacheMetrics.avgNetworkTimeMs.toFixed(0)
    : '0';
  const graphApiMsLabel = graphPerfMetrics.apiMs != null ? `${graphPerfMetrics.apiMs}ms` : '-';
  const graphRenderMsLabel = graphPerfMetrics.renderMs != null ? `${graphPerfMetrics.renderMs}ms` : '-';
  const graphTotalMsLabel = graphPerfMetrics.totalMs != null ? `${graphPerfMetrics.totalMs}ms` : '-';

  return (
    <CardContent>
      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <Box sx={{
          flex: '1 1 300px',
          minWidth: 300,
          p: 2,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1
        }}>
          <Typography
            variant="subtitle1"
            gutterBottom
            sx={{ fontWeight: 'bold', color: 'primary.main' }}
          >
            Número de Vizinhos: {levelFilter}
          </Typography>
          <Slider
            id="levelSlider"
            onChange={(e) => setLevelFilter(Number(e.target.value))}
            value={levelFilter}
            defaultValue={1}
            min={1}
            max={5}
            marks
            step={1}
            valueLabelDisplay="auto"
            sx={{
              '& .MuiSlider-thumb': {
                width: 24,
                height: 24,
              },
              '& .MuiSlider-track': {
                height: 6,
              },
              '& .MuiSlider-rail': {
                height: 6,
              }
            }}
          />
        </Box>

        <Box sx={{
          flex: '1 1 300px',
          minWidth: 300,
          p: 2,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 'bold', color: 'primary.main' }}
            >
              Buscar no grafo atual
            </Typography>
            <Tooltip title="Filtra apenas os nós já carregados neste grafo." arrow>
              <IconButton size="small" aria-label="Ajuda sobre busca no grafo">
                <HelpOutlineIcon fontSize="inherit" />
              </IconButton>
            </Tooltip>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <input
              type="text"
              placeholder="Digite nome ou documento."
              value={searchTerm}
              onChange={handleSearch}
              style={{
                flex: 1,
                padding: '8px 12px',
                border: '1px solid #ccc',
                borderRadius: '4px',
                fontSize: '14px'
              }}
            />
            <button
              onClick={handleResetView}
              style={{
                padding: '8px 16px',
                border: 'none',
                borderRadius: '4px',
                backgroundColor: '#4A90E2',
                color: 'white',
                cursor: 'pointer',
                fontSize: '14px',
                fontWeight: 'bold'
              }}
            >
              Reset
            </button>
          </Box>
        </Box>

        {IS_DEVELOPMENT && (
          <>
            <Box sx={{
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 1,
              minWidth: 180
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Estatísticas
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Total: <strong>{nodeCount}</strong> nós
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Visíveis: <strong>{visibleNodeCount}</strong> nós
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Arestas: <strong>{edgeCount}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Centro: <strong>{currentCenterNodeId || '-'}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Expansões: <strong>{expandedGraphData.length}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                API grafo: <strong>{graphApiMsLabel}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Render front: <strong>{graphRenderMsLabel}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Total: <strong>{graphTotalMsLabel}</strong>
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                Fonte: {graphPerfMetrics.source}
              </Typography>
              {isDrilldownMode && (
                <Typography variant="caption" sx={{ color: 'warning.main', display: 'block', mt: 1 }}>
                  Drill-down ativo. Hops aplicados: {effectiveHops}.
                </Typography>
              )}
            </Box>

            <Box sx={{
              p: 2,
              bgcolor: 'background.paper',
              borderRadius: 2,
              boxShadow: 1,
              minWidth: 220
            }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                Cache (Dev)
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Hit rate: <strong>{cacheHitRatePercent}%</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Hits/Misses: <strong>{cacheMetrics.cacheHits}</strong>/<strong>{cacheMetrics.cacheMisses}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Em voo: <strong>{cacheMetrics.inFlightHits}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Rede: <strong>{cacheMetrics.networkRequests}</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Média rede: <strong>{cacheAvgNetworkMs}ms</strong>
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                Erros rede: <strong>{cacheMetrics.networkErrors}</strong>
              </Typography>
              <Button
                variant="outlined"
                size="small"
                onClick={handleResetCacheMetrics}
                sx={{ mt: 1 }}
              >
                Reset métricas
              </Button>
              <Button
                variant="outlined"
                color="warning"
                size="small"
                onClick={handleResetCacheDataAndMetrics}
                sx={{ mt: 1 }}
              >
                Reset cache + métricas
              </Button>
            </Box>
          </>
        )}

        <Box sx={{
          p: 2,
          bgcolor: 'background.paper',
          borderRadius: 2,
          boxShadow: 1,
          minWidth: 180,
          display: 'flex',
          flexDirection: 'column',
          gap: 1
        }}>
          <Button
            variant="outlined"
            onClick={handleBackToInitialNode}
            disabled={(!isDrilldownMode && !hasExpandedBranches) || loadingGraph}
            size="small"
          >
            {isDrilldownMode ? 'Voltar ao Nó Inicial' : 'Limpar Expansões'}
          </Button>
        </Box>

      </Box>

      <Dialog open={isConfirmDialogOpen} onClose={handleCloseNodeNavigationDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Expandir Nó no Grafo Atual?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Você clicou no nó terminal <strong>{pendingNodeNavigation?.label}</strong>.
          </Typography>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Pré-visualizamos os relacionamentos desse nó no grafo de forma temporária.
          </Typography>
          {isPreviewLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: 1 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Carregando prévia de relacionamentos...
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ mb: 1 }}>
              Para manter o grafo limpo, a expansão só será persistida ao confirmar.
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            Hops no filtro atual: <strong>{pendingNodeNavigation?.currentHops ?? levelFilter}</strong>. Hops que serão usados na expansão: <strong>{pendingNodeNavigation?.appliedHops ?? Math.min(levelFilter, EXPANSION_HOPS_LIMIT)}</strong>.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNodeNavigationDialog} color="inherit">Cancelar</Button>
          <Button onClick={handleConfirmNodeNavigation} variant="contained" disabled={loadingGraph || isPreviewLoading}>Expandir</Button>
        </DialogActions>
      </Dialog>

      {/* Componente do Grafo */}
      {exceedsLimit ? (
        <Box sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '600px',
          bgcolor: 'background.paper',
          borderRadius: 2,
          border: '2px dashed',
          borderColor: 'warning.main',
          p: 4
        }}>
          <Typography variant="h5" sx={{ color: 'warning.main', fontWeight: 'bold', mb: 2 }}>
            ⚠️ Limite de Nós Excedido
          </Typography>
          <Typography variant="body1" sx={{ color: 'text.secondary', mb: 1, textAlign: 'center' }}>
            Este grafo contém <strong>{totalNodeCount} nós</strong>, mas o limite máximo para exibição é de <strong>{MAX_NODES} nós</strong>.
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'center', mb: 2 }}>
            Por favor, reduza o número de vizinhos no filtro acima para visualizar o grafo.
          </Typography>
          <Box sx={{
            bgcolor: 'info.light',
            p: 2,
            borderRadius: 1,
            mt: 2
          }}>
            <Typography variant="caption" sx={{ color: 'info.dark' }}>
              <strong>Dica:</strong> Diminua o valor do slider "Número de Vizinhos" para reduzir a quantidade de nós carregados.
            </Typography>
          </Box>
          <Button
            variant="contained"
            color="primary"
            onClick={handleExportToExcel}
            sx={{ mt: 3 }}
          >
            Baixar Todos os Registros (Excel)
          </Button>
        </Box>
      ) : elements && elements.length > 0 && !loadingGraph && !isLayoutRunning ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', xl: 'row' },
            gap: 2,
            alignItems: 'stretch',
          }}
        >
          <Box sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            boxShadow: 2,
            outline: 'none',
            flex: 1,
            minWidth: 0,
            '&:focus-visible': {
              boxShadow: '0 0 0 3px rgba(25, 118, 210, 0.35)'
            }
          }}>
            <Box
              tabIndex={0}
              role="region"
              aria-label="Área interativa do grafo"
              aria-keyshortcuts="ArrowUp ArrowDown ArrowLeft ArrowRight + - = _ 0 C"
              onKeyDown={handleGraphKeyboardNavigation}
            >
            <CytoscapeComponent
              key={`graph-${isGraphReady}`}
              elements={elements}
              stylesheet={activeStylesheet}
              style={{
                width: '100%',
                height: GRAPH_VIEWPORT_HEIGHT,
                backgroundColor: '#F5F7FA'
              }}
              cy={handleCyInit}
              layout={{
                name: 'preset'
              }}
            />
            </Box>
          </Box>

          <Box sx={{
            p: 2,
            bgcolor: 'background.paper',
            borderRadius: 2,
            boxShadow: 1,
            minWidth: { xs: '100%', xl: 220 },
            width: { xs: '100%', xl: 240 },
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
            alignSelf: 'flex-start',
            position: { xs: 'static', xl: 'sticky' },
            top: { xl: 16 }
          }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
              Acessibilidade do Grafo
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" size="small" onClick={handleZoomIn} aria-label="Aproximar grafo" aria-keyshortcuts="+ =">
                Zoom +
              </Button>
              <Button variant="outlined" size="small" onClick={handleZoomOut} aria-label="Afastar grafo" aria-keyshortcuts="- _">
                Zoom -
              </Button>
              <Button variant="outlined" size="small" onClick={handleFitGraph} aria-label="Ajustar grafo na tela" aria-keyshortcuts="0">
                Ajustar
              </Button>
              <Button variant="outlined" size="small" onClick={handleCenterGraph} aria-label="Centralizar grafo" aria-keyshortcuts="C">
                Centralizar
              </Button>
            </Box>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 32px)', gridTemplateRows: 'repeat(3, 32px)', gap: '2px' }}>
              <Box />
              <Button variant="outlined" size="small" onClick={() => handlePanGraph('up')} aria-label="Mover grafo para cima" aria-keyshortcuts="ArrowUp" sx={{ minWidth: 0, p: 0 }}>
                ↑
              </Button>
              <Box />
              <Button variant="outlined" size="small" onClick={() => handlePanGraph('left')} aria-label="Mover grafo para a esquerda" aria-keyshortcuts="ArrowLeft" sx={{ minWidth: 0, p: 0 }}>
                ←
              </Button>
              <Box />
              <Button variant="outlined" size="small" onClick={() => handlePanGraph('right')} aria-label="Mover grafo para a direita" aria-keyshortcuts="ArrowRight" sx={{ minWidth: 0, p: 0 }}>
                →
              </Button>
              <Box />
              <Button variant="outlined" size="small" onClick={() => handlePanGraph('down')} aria-label="Mover grafo para baixo" aria-keyshortcuts="ArrowDown" sx={{ minWidth: 0, p: 0 }}>
                ↓
              </Button>
              <Box />
            </Box>
          </Box>
        </Box>
      ) : (loadingGraph || isLayoutRunning) ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: GRAPH_VIEWPORT_HEIGHT }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 2
          }}>
            <CircularProgress />
            <Typography>
              {loadingGraph ? 'Carregando dados...' : 'Processando grafo...'}
            </Typography>
          </Box>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: GRAPH_VIEWPORT_HEIGHT }}>
          <Typography variant="body2" color="text.secondary">
            Nenhum dado de grafo disponível
          </Typography>
        </Box>
      )}
    </CardContent>
  );
};

GraphComponent.propTypes = {
  transactionData: PropTypes.object,
  onGraphNodesChange: PropTypes.func,
  onGraphEdgesChange: PropTypes.func,
  onCurrentCenterNodeChange: PropTypes.func,
};

export default GraphComponent;
